/** SUNTRONIX ONE — Corporate KYC & Vendor Registration Vault (admin).
 *
 *  Internal-only, tenant-scoped repository for SUNTRONIX's own
 *  corporate documents.  Provides upload, versioning, expiry tracking,
 *  data card management, reusable KYC pack composition, per-customer
 *  registration + checklist, customer-copy generation (with watermark),
 *  and a first-class audit trail.
 */
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  ShieldCheck, Upload, Search, Download, FileText, Eye, Package,
  Building2, RefreshCw, ClipboardList, ClockAlert, ShieldAlert,
  Users2, PlusCircle, X, Copy, History,
} from "lucide-react";
import { GovernedBanner, GovernedTag, useGovernedFields } from "@/components/kyc/GovernedField";

const STATUS_TONE = {
  ACTIVE:                 "bg-emerald-50 text-emerald-700 ring-emerald-200",
  EXPIRING_SOON: "bg-amber-50 text-amber-700 ring-amber-200",
  EXPIRED:               "bg-rose-50 text-rose-700 ring-rose-200",
  SUPERSEDED:      "bg-slate-100 text-slate-500 ring-slate-200",
  INACTIVE:            "bg-slate-100 text-slate-500 ring-slate-200",
};
const SENS_TONE = {
  GENERAL_INTERNAL:  "bg-sky-50 text-sky-700 ring-sky-200",
  RESTRICTED:              "bg-violet-50 text-violet-700 ring-violet-200",
  HIGHLY_RESTRICTED: "bg-rose-50 text-rose-700 ring-rose-200",
};
const TABS = [
  { id: "documents", label: "Documents", icon: FileText },
  { id: "packs",           label: "KYC Packs", icon: Package },
  { id: "datacard",   label: "Corporate Data Card", icon: Building2 },
  { id: "expiry",         label: "Expiry Queue", icon: ClockAlert },
  { id: "audit",           label: "Audit Trail", icon: History },
];

export default function KycVault() {
  const [tab, setTab] = useState("documents");
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    api.get("/kyc/meta").then(({ data }) => setMeta(data))
        .catch(() => toast.error("Failed to load KYC meta"));
  }, []);

  return (
    <div className="max-w-[1400px] mx-auto space-y-4" data-testid="kyc-vault">
      <header className="flex items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-lg bg-slate-900 text-white grid place-items-center">
              <ShieldCheck className="h-5 w-5" strokeWidth={1.6} />
            </div>
            <div>
              <div className="text-xl font-heading font-semibold text-slate-900">
                Corporate KYC &amp; Documents
              </div>
              <div className="text-[11px] text-slate-500">
                Internal — vendor registration and corporate document vault
              </div>
            </div>
          </div>
        </div>
        <div className="text-[11px] text-slate-500 uppercase tracking-wider">
          Suntronix internal · role-gated · audited
        </div>
      </header>

      <nav className="border-b border-slate-200 flex items-center gap-1 overflow-x-auto">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)}
              data-testid={`kyc-tab-${t.id}`}
              className={
                "px-3 py-2 text-[12px] font-medium border-b-2 -mb-[1px] flex items-center gap-1.5 whitespace-nowrap " +
                (tab === t.id
                    ? "border-slate-900 text-slate-900"
                    : "border-transparent text-slate-500 hover:text-slate-800")
              }>
            <t.icon className="h-3.5 w-3.5" strokeWidth={1.6} />
            {t.label}
          </button>
        ))}
      </nav>

      {tab === "documents" && <DocumentsTab meta={meta} />}
      {tab === "packs"           && <PacksTab meta={meta} />}
      {tab === "datacard"    && <DataCardTab />}
      {tab === "expiry"          && <ExpiryTab />}
      {tab === "audit"            && <AuditTab />}
    </div>
  );
}

/* ---------- DOCUMENTS TAB -------------------------------------------------- */
function DocumentsTab({ meta }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = { limit: 100 };
      if (q) params.q = q;
      if (category) params.category = category;
      if (status) params.status = status;
      const { data } = await api.get("/kyc/documents", { params });
      setRows(data.items || []);
    } catch { toast.error("Load failed"); }
    finally { setBusy(false); }
  }, [category, q, status]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-[420px]">
          <Search className="h-3.5 w-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="Search title, reference, category, entity"
              data-testid="kyc-search"
              className="w-full pl-8 pr-3 py-1.5 text-[12px] border border-slate-200 rounded-md focus:outline-none focus:border-slate-400" />
        </div>
        <select value={category} onChange={(e) => setCategory(e.target.value)}
            data-testid="kyc-filter-category"
            className="text-[12px] border border-slate-200 rounded-md px-2 py-1.5">
          <option value="">All categories</option>
          {(meta?.categories || []).map((c) =>
            <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)}
            data-testid="kyc-filter-status"
            className="text-[12px] border border-slate-200 rounded-md px-2 py-1.5">
          <option value="">All statuses</option>
          {(meta?.statuses || []).map((s) =>
            <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
        </select>
        <button onClick={load} data-testid="kyc-refresh"
            className="text-[12px] border border-slate-200 rounded-md px-3 py-1.5 hover:bg-slate-50 inline-flex items-center gap-1">
          <RefreshCw className={"h-3 w-3 " + (busy ? "animate-spin" : "")} />
          Refresh
        </button>
        <div className="flex-1" />
        <button onClick={() => setShowUpload(true)}
            data-testid="kyc-upload-open"
            className="text-[12px] bg-slate-900 text-white rounded-md px-3 py-1.5 hover:bg-slate-800 inline-flex items-center gap-1">
          <Upload className="h-3.5 w-3.5" strokeWidth={1.8} />
          Upload document
        </button>
      </div>

      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-[12px]">
          <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wider">
            <tr>
              <th className="text-left px-3 py-2">Title</th>
              <th className="text-left px-3 py-2">Category</th>
              <th className="text-left px-3 py-2">Reference</th>
              <th className="text-left px-3 py-2">Sensitivity</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Valid until</th>
              <th className="text-right px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !busy && (
              <tr><td colSpan={7} className="text-center py-6 text-slate-400">
                No documents yet. Upload your first KYC document.
              </td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50"
                data-testid={`kyc-row-${r.id}`}>
                <td className="px-3 py-2">
                  <div className="font-medium text-slate-900">{r.title}</div>
                  <div className="text-[10px] text-slate-500">v{r.version} · {r.doc_type}</div>
                </td>
                <td className="px-3 py-2 text-slate-600">{(r.category || "").replace(/_/g, " ")}</td>
                <td className="px-3 py-2 font-mono text-[11px] text-slate-600">{r.reference_no || "—"}</td>
                <td className="px-3 py-2">
                  <span className={"inline-flex px-1.5 py-0.5 rounded ring-1 text-[10px] " + (SENS_TONE[r.sensitivity] || "")}>
                    {(r.sensitivity || "").replace(/_/g, " ")}
                  </span>
                </td>
                <td className="px-3 py-2">
                  <span className={"inline-flex px-1.5 py-0.5 rounded ring-1 text-[10px] " + (STATUS_TONE[r.status] || "")}>
                    {(r.status || "").replace(/_/g, " ")}
                  </span>
                </td>
                <td className="px-3 py-2 text-slate-600 text-[11px]">{r.valid_until?.slice(0, 10) || "—"}</td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => setSelected(r.id)} data-testid={`kyc-open-${r.id}`}
                      className="text-slate-500 hover:text-slate-900 text-[11px] px-2 py-0.5 border border-slate-200 rounded">
                    Open
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showUpload && <UploadModal meta={meta} onClose={() => setShowUpload(false)}
                                                        onDone={() => { setShowUpload(false); load(); }} />}
      {selected && <DocumentDetail docId={selected}
                                                        onClose={() => setSelected(null)} onChange={load} />}
    </div>
  );
}

/* ---------- UPLOAD MODAL --------------------------------------------------- */
function UploadModal({ meta, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    title: "", category: "COMPANY_REGISTRATION", doc_type: "COMPANY_INCORPORATION",
    sensitivity: "GENERAL_INTERNAL", legal_entity: "SUNTRONIX",
    reference_no: "", issued_date: "", valid_until: "",
    issuing_authority: "", version: "1.0", notes: "",
    purpose: "", who_can_access: "", watermark_allowed: "true",
  });
  const fileRef = useRef(null);

  const submit = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    if (!fileRef.current?.files?.[0]) return toast.error("File is required");
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => v && fd.append(k, v));
      fd.append("file", fileRef.current.files[0]);
      const { data } = await api.post("/kyc/documents", fd,
          { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded: ${data.title}`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 grid place-items-center p-4"
        data-testid="kyc-upload-modal">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
          <div className="font-heading font-semibold text-slate-900">Upload KYC document</div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-4 grid grid-cols-2 gap-3 text-[12px]">
          <Field label="Title *" full>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                data-testid="kyc-up-title"
                className="w-full border border-slate-200 rounded-md px-2 py-1.5" />
          </Field>
          <Field label="Category *">
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full border border-slate-200 rounded-md px-2 py-1.5">
              {(meta?.categories || []).map((c) =>
                <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
            </select>
          </Field>
          <Field label="Document type *">
            <select value={form.doc_type} onChange={(e) => setForm({ ...form, doc_type: e.target.value })}
                className="w-full border border-slate-200 rounded-md px-2 py-1.5">
              {(meta?.doc_types || []).map((c) =>
                <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
            </select>
          </Field>
          <Field label="Sensitivity *">
            <select value={form.sensitivity} onChange={(e) => setForm({ ...form, sensitivity: e.target.value })}
                className="w-full border border-slate-200 rounded-md px-2 py-1.5">
              {(meta?.sensitivity || []).map((c) =>
                <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
            </select>
          </Field>
          <Field label="Legal entity">
            <input value={form.legal_entity} onChange={(e) => setForm({ ...form, legal_entity: e.target.value })}
                className="w-full border border-slate-200 rounded-md px-2 py-1.5" />
          </Field>
          <Field label="Reference number">
            <input value={form.reference_no} onChange={(e) => setForm({ ...form, reference_no: e.target.value })}
                className="w-full border border-slate-200 rounded-md px-2 py-1.5 font-mono" />
          </Field>
          <Field label="Issuing authority">
            <input value={form.issuing_authority} onChange={(e) => setForm({ ...form, issuing_authority: e.target.value })}
                className="w-full border border-slate-200 rounded-md px-2 py-1.5" />
          </Field>
          <Field label="Version">
            <input value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })}
                className="w-full border border-slate-200 rounded-md px-2 py-1.5" />
          </Field>
          <Field label="Issued date">
            <input type="date" value={form.issued_date} onChange={(e) => setForm({ ...form, issued_date: e.target.value })}
                className="w-full border border-slate-200 rounded-md px-2 py-1.5" />
          </Field>
          <Field label="Valid until">
            <input type="date" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })}
                className="w-full border border-slate-200 rounded-md px-2 py-1.5" />
          </Field>
          <Field label="Purpose (for new team members)" full>
            <input value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })}
                placeholder="e.g. Vendor onboarding tax verification"
                className="w-full border border-slate-200 rounded-md px-2 py-1.5" />
          </Field>
          <Field label="Who usually uses this" full>
            <input value={form.who_can_access} onChange={(e) => setForm({ ...form, who_can_access: e.target.value })}
                placeholder="e.g. Sales, Finance, Vendor Registration"
                className="w-full border border-slate-200 rounded-md px-2 py-1.5" />
          </Field>
          <Field label="Notes" full>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2} className="w-full border border-slate-200 rounded-md px-2 py-1.5" />
          </Field>
          <Field label="File (PDF/DOCX/XLSX/PNG/JPG · max 40 MB)" full>
            <input type="file" ref={fileRef} data-testid="kyc-up-file"
                className="w-full text-[11px]" />
          </Field>
          <Field label="Allow customer watermark" full>
            <label className="text-[11px] inline-flex items-center gap-2">
              <input type="checkbox"
                  checked={form.watermark_allowed === "true"}
                  onChange={(e) => setForm({ ...form, watermark_allowed: e.target.checked ? "true" : "false" })} />
              A watermarked customer copy may be generated from this document
            </label>
          </Field>
        </div>
        <div className="p-4 border-t border-slate-200 flex items-center justify-end gap-2 sticky bottom-0 bg-white">
          <button onClick={onClose} className="text-[12px] px-3 py-1.5 rounded-md border border-slate-200">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="kyc-up-submit"
              className="text-[12px] px-3 py-1.5 rounded-md bg-slate-900 text-white disabled:opacity-50">
            {busy ? "Uploading…" : "Upload"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{label}</div>
      {children}
    </div>
  );
}

/* ---------- DOCUMENT DETAIL DRAWER ---------------------------------------- */
function DocumentDetail({ docId, onClose, onChange }) {
  const [doc, setDoc] = useState(null);
  const [cid, setCid] = useState("");
  const [customerList, setCustomerList] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/kyc/documents/${docId}`);
      setDoc(data);
    } catch { toast.error("Load failed"); }
  }, [docId]);
  useEffect(() => { load(); }, [docId, load]);

  useEffect(() => {
    api.get("/vtiger/crm-customers?limit=25").then(({ data }) =>
        setCustomerList(data.items || [])).catch(() => {});
  }, []);

  const streamFile = async (path, filename) => {
    try {
      const r = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.response?.data?.detail || "Download failed"); }
  };

  const verify = async () => {
    setBusy(true);
    try {
      await api.post(`/kyc/documents/${docId}/verify`);
      toast.success("Document marked verified");
      await load(); onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Verify failed"); }
    finally { setBusy(false); }
  };

  const createCopy = async (watermark) => {
    if (!cid) return toast.error("Pick a customer");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("customer_id", cid);
      fd.append("watermark", watermark ? "true" : "false");
      const { data } = await api.post(`/kyc/documents/${docId}/customer-copy`, fd);
      toast.success("Customer copy generated");
      streamFile(`/kyc/customer-copies/${data.id}/download`,
          `${data.customer_name || "customer"}-${data.id.slice(0,8)}${data.file_ext || ""}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  if (!doc) return null;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex justify-end"
        data-testid="kyc-doc-detail">
      <div className="bg-white w-full max-w-xl h-full overflow-y-auto shadow-2xl">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
          <div>
            <div className="font-heading font-semibold text-slate-900">{doc.title}</div>
            <div className="text-[10px] text-slate-500">
              {doc.doc_type?.replace(/_/g, " ")} · {doc.category?.replace(/_/g, " ")} · v{doc.version}
            </div>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-3 text-[11px]">
            <Row label="Reference" value={doc.reference_no} mono />
            <Row label="Legal entity" value={doc.legal_entity} />
            <Row label="Issuing authority" value={doc.issuing_authority} />
            <Row label="Issued" value={doc.issued_date} />
            <Row label="Valid from" value={doc.valid_from} />
            <Row label="Valid until" value={doc.valid_until} />
            <Row label="Sensitivity" value={doc.sensitivity?.replace(/_/g, " ")} />
            <Row label="Status" value={doc.status?.replace(/_/g, " ")} />
            <Row label="Uploaded" value={`${doc.uploader_name || "—"} · ${doc.created_at?.slice(0, 10)}`} full />
            <Row label="Verified" value={doc.verified_by
                    ? `${doc.verified_by} · ${doc.verified_on?.slice(0, 10)}`
                    : "Not verified"} full />
            <Row label="Purpose" value={doc.purpose} full />
            <Row label="Usually accessed by" value={doc.who_can_access} full />
            <Row label="Notes" value={doc.notes} full />
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {doc.can_download && (
              <>
                <button onClick={() =>
                        streamFile(`/kyc/documents/${docId}/preview`, doc.filename)}
                    data-testid="kyc-preview"
                    className="text-[11px] px-3 py-1.5 rounded-md border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-1">
                  <Eye className="h-3 w-3" /> Preview
                </button>
                <button onClick={() =>
                        streamFile(`/kyc/documents/${docId}/download`, doc.filename)}
                    data-testid="kyc-download"
                    className="text-[11px] px-3 py-1.5 rounded-md bg-slate-900 text-white hover:bg-slate-800 inline-flex items-center gap-1">
                  <Download className="h-3 w-3" /> Download
                </button>
              </>
            )}
            {!doc.can_download && (
              <div className="text-[11px] text-rose-600 inline-flex items-center gap-1">
                <ShieldAlert className="h-3 w-3" />
                Your role cannot download this sensitivity classification
              </div>
            )}
            <button onClick={() =>
                    navigator.clipboard.writeText(doc.reference_no || "").then(
                        () => toast.success("Reference copied"))}
                data-testid="kyc-copy-ref"
                className="text-[11px] px-3 py-1.5 rounded-md border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-1">
              <Copy className="h-3 w-3" /> Copy reference
            </button>
            {!doc.verified_by && (
              <button onClick={verify} disabled={busy}
                  data-testid="kyc-verify"
                  className="text-[11px] px-3 py-1.5 rounded-md border border-emerald-200 text-emerald-700 hover:bg-emerald-50">
                Mark verified
              </button>
            )}
          </div>

          {doc.can_download && (
            <div className="p-3 border border-slate-200 rounded-lg space-y-2">
              <div className="text-[11px] font-semibold text-slate-900">Create customer copy</div>
              <div className="flex items-center gap-2">
                <select value={cid} onChange={(e) => setCid(e.target.value)}
                    data-testid="kyc-copy-customer"
                    className="text-[11px] border border-slate-200 rounded px-2 py-1 flex-1">
                  <option value="">Select customer…</option>
                  {customerList.map((c) =>
                    <option key={c.id} value={c.id}>{c.account_name || c.name || c.customer_code}</option>)}
                </select>
                <button onClick={() => createCopy(true)} disabled={busy || !cid}
                    data-testid="kyc-copy-watermark"
                    className="text-[11px] px-2 py-1 rounded bg-violet-600 text-white disabled:opacity-40">
                  Copy + watermark
                </button>
                <button onClick={() => createCopy(false)} disabled={busy || !cid}
                    className="text-[11px] px-2 py-1 rounded border border-slate-200 disabled:opacity-40">
                  Clean copy
                </button>
              </div>
            </div>
          )}

          {(doc.previous_versions || []).length > 0 && (
            <div>
              <div className="text-[11px] font-semibold text-slate-900 mb-1">Version history</div>
              <div className="space-y-1">
                {doc.previous_versions.map((p) => (
                  <div key={p.id} className="text-[11px] text-slate-500 flex items-center justify-between border-b border-slate-100 py-1">
                    <span>v{p.version} · {p.created_at?.slice(0, 10)}</span>
                    <span className="uppercase tracking-wider text-[9px]">{p.status?.replace(/_/g, " ")}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, mono, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <div className="text-[9px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={"text-slate-900 truncate " + (mono ? "font-mono" : "")}>{value || "—"}</div>
    </div>
  );
}

/* ---------- PACKS TAB ----------------------------------------------------- */
function PacksTab({ meta }) {
  void meta;
  const [packs, setPacks] = useState([]);
  const [docs, setDocs] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const [p, d, c] = await Promise.all([
        api.get("/kyc/packs"),
        api.get("/kyc/documents?limit=200"),
        api.get("/vtiger/crm-customers?limit=50"),
      ]);
      setPacks(p.data.items || []);
      setDocs(d.data.items || []);
      setCustomers(c.data.items || []);
    } catch { toast.error("Load failed"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const generate = async (packId, customerId) => {
    if (!customerId) return toast.error("Pick a customer");
    try {
      const fd = new FormData();
      fd.append("customer_id", customerId);
      fd.append("watermark_sensitive", "true");
      const { data } = await api.post(`/kyc/packs/${packId}/generate`, fd);
      toast.success(`Pack generated (${(data.size / 1024).toFixed(1)} KB)`);
      const r = await api.get(`/kyc/pack-generations/${data.id}/download`,
          { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `suntronix-kyc-${(data.customer_name || "customer").replace(/\s+/g, "_")}-${data.id.slice(0,8)}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.response?.data?.detail || "Generate failed"); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="text-[11px] text-slate-500">
          Compose reusable document bundles that Sales / Finance can send with one click.
        </div>
        <div className="flex-1" />
        <button onClick={() => setShowNew(true)}
            data-testid="kyc-pack-new"
            className="text-[12px] bg-slate-900 text-white rounded-md px-3 py-1.5 hover:bg-slate-800 inline-flex items-center gap-1">
          <PlusCircle className="h-3.5 w-3.5" /> New pack
        </button>
        <button onClick={load}
            className="text-[12px] border border-slate-200 rounded-md px-3 py-1.5 hover:bg-slate-50 inline-flex items-center gap-1">
          <RefreshCw className={"h-3 w-3 " + (busy ? "animate-spin" : "")} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {packs.length === 0 && !busy && (
          <div className="col-span-2 text-center py-10 text-slate-400 text-[12px] border border-dashed border-slate-200 rounded-lg">
            No KYC packs yet. Configure a Standard Vendor KYC Pack to get started.
          </div>
        )}
        {packs.map((p) => (
          <PackCard key={p.id} pack={p} docs={docs} customers={customers}
                                onGenerate={(cid) => generate(p.id, cid)} onReload={load} />
        ))}
      </div>

      {showNew && <PackEditor docs={docs} onClose={() => setShowNew(false)}
                                        onDone={() => { setShowNew(false); load(); }} />}
    </div>
  );
}

function PackCard({ pack, docs, customers, onGenerate, onReload }) {
  void onReload;
  const [cid, setCid] = useState("");
  const included = docs.filter((d) => (pack.document_ids || []).includes(d.id));
  return (
    <div className="border border-slate-200 rounded-lg p-3 space-y-2" data-testid={`kyc-pack-${pack.id}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="font-semibold text-slate-900 text-[13px]">{pack.name}</div>
          <div className="text-[10px] text-slate-500">{pack.description}</div>
        </div>
        <div className="text-[10px] text-slate-500 uppercase tracking-wider">
          {included.length} docs
        </div>
      </div>
      <ul className="text-[11px] text-slate-600 space-y-0.5 pl-3 list-disc">
        {included.slice(0, 6).map((d) => (
          <li key={d.id}>{d.title} <span className="text-slate-400">· {d.category?.replace(/_/g, " ")}</span></li>
        ))}
        {included.length > 6 && <li className="text-slate-400">+{included.length - 6} more…</li>}
      </ul>
      <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
        <select value={cid} onChange={(e) => setCid(e.target.value)}
            data-testid={`kyc-pack-customer-${pack.id}`}
            className="flex-1 text-[11px] border border-slate-200 rounded px-2 py-1">
          <option value="">Choose customer…</option>
          {customers.map((c) =>
            <option key={c.id} value={c.id}>{c.account_name || c.name || c.customer_code}</option>)}
        </select>
        <button onClick={() => onGenerate(cid)} disabled={!cid}
            data-testid={`kyc-pack-gen-${pack.id}`}
            className="text-[11px] px-3 py-1 bg-slate-900 text-white rounded disabled:opacity-40">
          Create pack
        </button>
      </div>
    </div>
  );
}

function PackEditor({ docs, onClose, onDone }) {
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [selected, setSelected] = useState({});
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const ids = Object.keys(selected).filter((k) => selected[k]);
    if (!name.trim() || ids.length === 0)
      return toast.error("Give it a name and pick at least one document");
    setBusy(true);
    try {
      await api.post("/kyc/packs",
          { name: name.trim(), description: desc, document_ids: ids, active: true });
      toast.success("Pack created"); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 grid place-items-center p-4"
        data-testid="kyc-pack-editor">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <div className="font-heading font-semibold text-slate-900">New KYC Pack</div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-4 space-y-3 flex-1 overflow-y-auto">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Name *</div>
            <input value={name} onChange={(e) => setName(e.target.value)}
                data-testid="kyc-pack-name"
                className="w-full border border-slate-200 rounded-md px-2 py-1.5 text-[12px]" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Description</div>
            <input value={desc} onChange={(e) => setDesc(e.target.value)}
                className="w-full border border-slate-200 rounded-md px-2 py-1.5 text-[12px]" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Documents to include</div>
            <div className="border border-slate-200 rounded-md max-h-[300px] overflow-y-auto divide-y divide-slate-100">
              {docs.map((d) => (
                <label key={d.id} className="flex items-center gap-2 px-3 py-1.5 text-[11px] hover:bg-slate-50 cursor-pointer">
                  <input type="checkbox" checked={!!selected[d.id]}
                      onChange={(e) => setSelected({ ...selected, [d.id]: e.target.checked })} />
                  <span className="flex-1">{d.title}</span>
                  <span className="text-[10px] text-slate-400">{d.category?.replace(/_/g, " ")}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="p-4 border-t border-slate-200 flex items-center justify-end gap-2">
          <button onClick={onClose} className="text-[12px] px-3 py-1.5 rounded-md border border-slate-200">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="kyc-pack-submit"
              className="text-[12px] px-3 py-1.5 rounded-md bg-slate-900 text-white disabled:opacity-50">
            {busy ? "Saving…" : "Create pack"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------- DATA CARD TAB ------------------------------------------------- */
const CARD_SECTIONS = [
  { title: "Identity", fields: [
    ["legal_business_name", "Legal business name"],
    ["trade_name", "Trade name"],
    ["business_constitution", "Business constitution"],
    ["year_established", "Year established"],
    ["nature_of_business", "Nature of business"],
  ]},
  { title: "Statutory", fields: [
    ["pan", "PAN"], ["gstin", "GSTIN"], ["udyam_msme", "Udyam / MSME"],
    ["authorised_signatory", "Authorised signatory"],
  ]},
  { title: "Address", fields: [
    ["registered_address", "Registered address"],
    ["operating_address", "Operating address"],
    ["city", "City"], ["state", "State"],
    ["postal_code", "Postal code"], ["country", "Country"],
    ["website", "Website"],
  ]},
  { title: "Contact", fields: [
    ["primary_contact", "Primary contact"],
    ["primary_email", "Primary email"],
    ["primary_phone", "Primary phone"],
    ["finance_contact", "Finance contact"],
    ["finance_email", "Finance email"],
  ]},
  { title: "Banking", fields: [
    ["bank_account_name", "Bank account name"],
    ["bank_name", "Bank name"],
    ["account_number", "Account number"],
    ["ifsc", "IFSC"], ["swift", "SWIFT"],
  ]},
];

function DataCardTab() {
  const [card, setCard] = useState({});
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const governed = useGovernedFields("kyc_data_card");
  useEffect(() => { api.get("/kyc/data-card").then(({ data }) => setCard(data || {})); }, []);
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/kyc/data-card", Object.fromEntries(Object.entries(card).filter(([k]) => !governed[k])));
      toast.success("Corporate data card saved · governed fields come from KYC Readiness");
      setDirty(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-4" data-testid="kyc-data-card">
      <div className="text-[11px] text-slate-500">
        Reusable structured data for customer vendor-registration portals.
        Every field has a copy button — sensitive banking fields are role-restricted.
      </div>
      <GovernedBanner count={Object.keys(governed).length} testid="kyc-card-governed-banner" />
      {CARD_SECTIONS.map((sec) => (
        <div key={sec.title} className="border border-slate-200 rounded-lg p-4">
          <div className="text-[11px] font-heading font-semibold text-slate-900 mb-2">{sec.title}</div>
          <div className="grid grid-cols-2 gap-3">
            {sec.fields.map(([k, label]) => (
              <div key={k}>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5 flex items-center justify-between gap-2">{label}{governed[k] && <GovernedTag masterKey={governed[k]} testid={`kyc-card-governed-${k}`} />}</div>
                <div className="flex items-center gap-1">
                  <input value={card[k] || ""}
                      readOnly={!!governed[k]}
                      onChange={(e) => { if (governed[k]) return; setCard({ ...card, [k]: e.target.value }); setDirty(true); }}
                      data-testid={`kyc-card-${k}`}
                      className={`flex-1 border rounded-md px-2 py-1.5 text-[12px] ${governed[k] ? "border-emerald-200 bg-emerald-50/60 text-slate-700 cursor-not-allowed" : "border-slate-200"}`} />
                  <button onClick={() =>
                          navigator.clipboard.writeText(card[k] || "")
                              .then(() => toast.success(`${label} copied`))}
                      title="Copy" className="p-1.5 rounded border border-slate-200 hover:bg-slate-50">
                    <Copy className="h-3 w-3" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
      <div className="sticky bottom-0 bg-white/95 backdrop-blur pt-3 pb-1 border-t border-slate-200 flex items-center justify-end gap-2">
        <button onClick={save} disabled={!dirty || busy}
            data-testid="kyc-card-save"
            className="text-[12px] px-3 py-1.5 rounded-md bg-slate-900 text-white disabled:opacity-40">
          {busy ? "Saving…" : "Save corporate data"}
        </button>
      </div>
    </div>
  );
}

/* ---------- EXPIRY QUEUE ---------------------------------------------------- */
function ExpiryTab() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/kyc/expiry").then(({ data }) => setData(data)); }, []);
  const section = (title, items, tone) => (
    <div className="border border-slate-200 rounded-lg p-3">
      <div className={"text-[11px] font-semibold mb-2 " + tone}>{title} · {items?.length || 0}</div>
      {(!items || items.length === 0) && <div className="text-[11px] text-slate-400">Nothing here — nice.</div>}
      {items?.map((d) => (
        <div key={d.id} className="text-[11px] flex items-center justify-between border-b border-slate-100 py-1">
          <div className="truncate max-w-[70%]">
            <span className="font-medium">{d.title}</span>
            <span className="text-slate-400"> · {d.category?.replace(/_/g, " ")}</span>
          </div>
          <div className="text-slate-500">{d.valid_until?.slice(0, 10)}</div>
        </div>
      ))}
    </div>
  );
  if (!data) return <div className="text-[11px] text-slate-500 py-6">Loading…</div>;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="kyc-expiry">
      {section("Expired", data.expired, "text-rose-600")}
      {section("Expiring in 30 days", data.in_30_days, "text-rose-500")}
      {section("Expiring in 60 days", data.in_60_days, "text-amber-600")}
      {section("Expiring in 90 days", data.in_90_days, "text-amber-500")}
    </div>
  );
}

/* ---------- AUDIT ---------------------------------------------------------- */
function AuditTab() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/kyc/audit?limit=200");
      setRows(data.items || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);
  return (
    <div className="space-y-2" data-testid="kyc-audit">
      <button onClick={load} className="text-[11px] border border-slate-200 rounded px-2 py-1 inline-flex items-center gap-1">
        <RefreshCw className={"h-3 w-3 " + (busy ? "animate-spin" : "")} /> Refresh
      </button>
      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-[11px]">
          <thead className="bg-slate-50 text-slate-500 text-[9px] uppercase tracking-wider">
            <tr>
              <th className="text-left px-3 py-2">Time</th>
              <th className="text-left px-3 py-2">Actor</th>
              <th className="text-left px-3 py-2">Role</th>
              <th className="text-left px-3 py-2">Action</th>
              <th className="text-left px-3 py-2">Document</th>
              <th className="text-left px-3 py-2">Customer</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100">
                <td className="px-3 py-1.5 font-mono text-slate-500">{r.at?.slice(0, 19).replace("T", " ")}</td>
                <td className="px-3 py-1.5">{r.actor_name || r.actor}</td>
                <td className="px-3 py-1.5 text-slate-500">{r.actor_role}</td>
                <td className="px-3 py-1.5 font-medium">{r.action}</td>
                <td className="px-3 py-1.5 font-mono text-slate-500">{r.document_id?.slice(0, 8) || "—"}</td>
                <td className="px-3 py-1.5 font-mono text-slate-500">{r.customer_id?.slice(0, 8) || "—"}</td>
              </tr>
            ))}
            {rows.length === 0 && !busy && (
              <tr><td colSpan={6} className="text-center py-6 text-slate-400">
                Audit trail is restricted to admin/finance.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
